import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useHashFile from '../hooks/useHashFile';
import usePinataUpload from '../hooks/usePinataUpload';
import useOCR from '../hooks/useOCR';
import useAuthentication from '../hooks/useAuthentication';
import { useCertificateDB } from '../context/CertificateContext';
import { useActivity } from '../context/ActivityContext';
import { useDatabase } from '../context/DatabaseContext';
import { anchorHash } from '../services/anchorClient';
import { insertCertificate } from '../services/supabaseCertificates';
import { testSupabaseInsert } from '../services/testSupabaseInsert';

const UploadPage = () => {
  const [institution, setInstitution] = useState('');
  const [studentName, setStudentName] = useState('');
  const [rollNumber, setRollNumber] = useState('');
  const [certificateType, setCertificateType] = useState('Degree');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [showOCRData, setShowOCRData] = useState(false);
  
  const { hashFile, isHashing } = useHashFile();
  const { uploadToPinata, isUploading, uploadError, testPinataConnection } = usePinataUpload();
  const { extractTextFromImage, isProcessing: ocrProcessing, extractedData, error: ocrError, clearData } = useOCR();
  const { authenticateCertificate, authResult } = useAuthentication();
  const { addCertificate } = useCertificateDB();
  const { log } = useActivity();
  const { getInstitutions, addCertificate: addToDatabase, findStudent, addStudent, findCertificateByHash } = useDatabase();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      setFile(droppedFile);
      
      // Clear previous OCR data
      clearData();
      setShowOCRData(false);
      
      // If it's an image file or PDF, run OCR automatically
      if (droppedFile.type.startsWith('image/') || droppedFile.type === 'application/pdf') {
        console.log('Image/PDF file detected, running OCR...');
        try {
          await extractTextFromImage(droppedFile);
          setShowOCRData(true);
        } catch (error) {
          console.warn('OCR failed:', error);
        }
      }
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Clear previous OCR data
      clearData();
      setShowOCRData(false);
      
      // If it's an image file or PDF, run OCR and authentication
      if (selectedFile.type.startsWith('image/') || selectedFile.type === 'application/pdf') {
        console.log('Image/PDF file detected, running OCR and authentication...');
        try {
          // Run OCR and authentication in parallel
          await Promise.all([
            extractTextFromImage(selectedFile),
            authenticateCertificate(selectedFile)
          ]);
          setShowOCRData(true);
        } catch (error) {
          console.warn('Processing failed:', error);
        }
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !institution) {
      alert('Please select an institution and a file.');
      return;
    }

    try {
      setUploadSuccess(null);
      
      // Test Pinata connection first
      console.log('Testing Pinata connection...');
      const connectionTest = await testPinataConnection();
      if (!connectionTest) {
        throw new Error('Failed to connect to Pinata. Please check your API credentials.');
      }
      
      // Step 1: Generate SHA256 hash
      console.log('Generating file hash...');
      const fileHashRaw = await hashFile(file);
      const fileHash = fileHashRaw?.toString().trim().toLowerCase();
      console.log('File hash generated:', fileHash);

      // Step 1.5: Duplicate check
      const existing = findCertificateByHash(fileHash);
      if (existing) {
        setUploadSuccess({ duplicateOf: existing, fileHash });
        alert('This file is already in the system (duplicate detected by hash).');
        log({ action: 'upload-duplicate', institution, fileHash, cid: existing.cid, student: studentName || 'Unknown' });
        return;
      }
      
      // Step 2: Upload to Pinata with OCR metadata
      console.log('Uploading to Pinata...');
      const uploadResult = await uploadToPinata(file, institution, extractedData);
      console.log('Upload successful:', uploadResult);
      
      // Anchor on blockchain (stub)
      const anchor = await anchorHash(fileHash);
      
      // Step 3: Check if student exists, create if not (only if we have roll number)
      let student = null;
      if (rollNumber) {
        student = findStudent(rollNumber, institution);
        if (!student && studentName) {
          student = addStudent({
            firstName: studentName.split(' ')[0] || 'Unknown',
            lastName: studentName.split(' ').slice(1).join(' ') || '',
            rollNumber: rollNumber,
            institutionId: institution,
            program: extractedData?.program || '',
            batch: extractedData?.batch || ''
          });
        }
      }
      
      // Step 4: Create certificate record with OCR and authentication data
      const certificateRecord = {
        institution: institution,
        institutionId: institution,
        studentId: student?.id || null,
        studentName: (studentName && studentName.trim()) ? studentName : (extractedData?.studentName || 'Unknown'),
        rollNumber: (rollNumber && rollNumber.trim()) ? rollNumber : (extractedData?.rollNumber || 'N/A'),
        certificateType: (certificateType && certificateType.trim()) ? certificateType : (extractedData?.certificateType || ''),
        fileName: file.name,
        fileHash: fileHash,
        cid: uploadResult.IpfsHash,
        ipfsUrl: `https://gateway.pinata.cloud/ipfs/${uploadResult.IpfsHash}`,
        uploadDate: new Date().toISOString(),
        anchorTx: anchor.txId,
        anchorUrl: anchor.url,
        // Include OCR data in the record
        ...(extractedData && {
          ocrData: extractedData,
          marks: extractedData.marks,
          program: extractedData.program,
          batch: extractedData.batch
        }),
        // Include authentication results
        ...(authResult && {
          authenticationResult: {
            isAuthentic: authResult.isAuthentic,
            confidence: authResult.confidence,
            prediction: authResult.prediction,
            isDemo: authResult.isDemo || false
          }
        })
      };
      
      // Step 5: Save to both databases
      addCertificate(certificateRecord); // Legacy database
      addToDatabase(certificateRecord); // New comprehensive database
      
      // Cloud DB (Supabase) - with detailed error logging
      try {
        console.log('Attempting to save to Supabase...');
        const supabaseResult = await insertCertificate(certificateRecord);
        console.log('Supabase save result:', supabaseResult);
        if (supabaseResult?.skipped) {
          console.warn('Supabase save was skipped:', supabaseResult);
        } else {
          console.log('✓ Certificate successfully saved to Supabase');
        }
      } catch (e) {
        console.error('❌ Supabase insert failed:', e);
        console.error('Error details:', {
          message: e.message,
          code: e.code,
          details: e.details,
          hint: e.hint
        });
        // Don't fail the whole upload, just log it
      }
      log({ 
        action: 'upload', 
        institution, 
        fileHash, 
        cid: uploadResult.IpfsHash, 
        anchorTx: anchor.txId, 
        student: studentName || 'Unknown',
        rollNumber: rollNumber || 'N/A',
        certificateType: certificateType,
        authentic: authResult?.isAuthentic || null,
        confidence: authResult?.confidence || null
      });
      
      setUploadSuccess(certificateRecord);
      setFile(null);
      
    } catch (error) {
      console.error('Upload failed:', error);
      alert(`Upload failed: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      {/* Wave Background */}
      <div className="page-wave-background">
        <img src="/hero-wave.svg" alt="" className="wave-image" />
      </div>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Upload Certificate</h1>
          <p className="text-gray-600">Select an institution and upload your certificate to IPFS</p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Institution Selection */}
          <div className="mb-6">
            <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-2">
              Institution *
            </label>
            <select
              id="institution"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              className="form-select"
              required
            >
              <option value="">Select Institution</option>
              {getInstitutions().map(inst => (
                <option key={inst.id} value={inst.id}>
                  {inst.name} - {inst.classification} ({inst.category})
                </option>
              ))}
            </select>
          </div>

          {/* Student Information - Optional */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="studentName" className="block text-sm font-medium text-gray-700 mb-2">
                Student Name (Optional)
              </label>
              <input
                type="text"
                id="studentName"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                className="form-input"
                placeholder="Enter full name (optional)"
              />
            </div>
            <div>
              <label htmlFor="rollNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Roll Number (Optional)
              </label>
              <input
                type="text"
                id="rollNumber"
                value={rollNumber}
                onChange={(e) => setRollNumber(e.target.value)}
                className="form-input"
                placeholder="Enter roll number (optional)"
              />
            </div>
          </div>

          {/* Certificate Type */}
          <div className="mb-6">
            <label htmlFor="certificateType" className="block text-sm font-medium text-gray-700 mb-2">
              Certificate Type
            </label>
            <select
              id="certificateType"
              value={certificateType}
              onChange={(e) => setCertificateType(e.target.value)}
              className="form-select"
            >
              <option value="Degree">Degree Certificate</option>
              <option value="Diploma">Diploma Certificate</option>
              <option value="Marksheet">Marksheet</option>
              <option value="Provisional">Provisional Certificate</option>
              <option value="Character">Character Certificate</option>
              <option value="Migration">Migration Certificate</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certificate File
            </label>
            <div
              className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                dragActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      {file ? file.name : 'Drop files here, or click to select'}
                    </span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept="image/*,.pdf"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    PNG, JPG, PDF up to 10MB (OCR works best with images)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* OCR Results Display */}
          {showOCRData && extractedData && (
            <div className="mb-6 bg-white border-2 border-purple-200 rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4">
                <div className="flex items-center">
                  <svg className="w-6 h-6 mr-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-xl font-bold text-white">📄 OCR Extracted Information</h3>
                </div>
                <p className="mt-1 text-purple-100 text-sm">Automatically extracted from your certificate</p>
              </div>
              
              <div className="p-6">
                {ocrProcessing && (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
                    <p className="mt-4 text-lg text-purple-600 font-medium">Processing OCR...</p>
                    <p className="mt-1 text-sm text-gray-500">Analyzing document content</p>
                  </div>
                )}
                
                {!ocrProcessing && (
                  <div className="space-y-4">
                    {/* Primary Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-center mb-2">
                          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <strong className="text-sm font-semibold text-blue-900">Student Name</strong>
                        </div>
                        <p className="text-lg font-medium text-blue-900 ml-7">{extractedData.studentName || 'Not detected'}</p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
                        <div className="flex items-center mb-2">
                          <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                          </svg>
                          <strong className="text-sm font-semibold text-indigo-900">Roll Number</strong>
                        </div>
                        <p className="text-lg font-medium text-indigo-900 ml-7">{extractedData.rollNumber || 'Not detected'}</p>
                      </div>
                    </div>
                    
                    {/* Secondary Information */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center mb-2">
                          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <strong className="text-xs font-semibold text-gray-700 uppercase">Certificate Type</strong>
                        </div>
                        <p className="text-sm font-medium text-gray-900 ml-6">{extractedData.certificateType || 'Not detected'}</p>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center mb-2">
                          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <strong className="text-xs font-semibold text-gray-700 uppercase">Issue Date</strong>
                        </div>
                        <p className="text-sm font-medium text-gray-900 ml-6">{extractedData.issueDate || 'Not detected'}</p>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center mb-2">
                          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <strong className="text-xs font-semibold text-gray-700 uppercase">Marks/Grade</strong>
                        </div>
                        <p className="text-sm font-medium text-gray-900 ml-6">{extractedData.marks || 'Not detected'}</p>
                      </div>
                    </div>
                    
                    {/* Additional Details */}
                    {(extractedData.program || extractedData.batch) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        {extractedData.program && (
                          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                            <strong className="text-xs font-semibold text-green-700 uppercase">Program</strong>
                            <p className="text-sm font-medium text-green-900 mt-1">{extractedData.program}</p>
                          </div>
                        )}
                        {extractedData.batch && (
                          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                            <strong className="text-xs font-semibold text-yellow-700 uppercase">Batch</strong>
                            <p className="text-sm font-medium text-yellow-900 mt-1">{extractedData.batch}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Debug: Show raw OCR text */}
                {extractedData.raw && (
                  <details className="mt-6">
                    <summary className="cursor-pointer text-sm text-purple-700 font-semibold hover:text-purple-900 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
                      </svg>
                      View Full Extracted Text
                    </summary>
                    <div className="mt-3 p-4 bg-gray-900 rounded-lg text-gray-100 text-xs font-mono whitespace-pre-wrap max-h-60 overflow-y-auto shadow-inner">
                      {extractedData.raw}
                    </div>
                  </details>
                )}
                
                {ocrError && (
                  <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-sm font-medium text-red-700">
                        OCR Error: {ocrError}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-purple-800">
                      <strong className="font-semibold">Auto-detected information:</strong> This data will be automatically included as metadata with your certificate, making it easier to search and verify later.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Authentication Results Display - Removed for cleaner upload experience */}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!file || !institution || isHashing || isUploading}
            className="btn-primary w-full"
            style={{backgroundColor: !file || !institution || isHashing || isUploading ? '#9ca3af' : '#2563eb'}}
          >
            {isHashing ? 'Generating Hash...' : isUploading ? 'Uploading to IPFS...' : 'Upload Certificate'}
          </button>

          {/* Test Connection Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
            <button
              onClick={async () => {
                console.log('Testing Pinata connection...');
                const result = await testPinataConnection();
                if (result) {
                  alert('✅ Pinata connection successful!');
                } else {
                  alert('❌ Pinata connection failed. Check console for details.');
                }
              }}
              className="px-4 py-2 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              type="button"
            >
              🔗 Test Pinata
            </button>
            
            <button
              onClick={async () => {
                console.log('\n🧪 Running Supabase diagnostic test...\n');
                const result = await testSupabaseInsert();
                if (result.success) {
                  alert('✅ Supabase is working!\n\nCertificates will be saved to the database.\n\nCheck console for details.');
                } else {
                  alert('❌ Supabase test failed!\n\nError: ' + (result.error || 'Unknown') + '\n\nCheck console for detailed diagnostics and solutions.');
                }
              }}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              type="button"
            >
              🗄️ Test Supabase
            </button>
          </div>

          {/* Error Display */}
          {uploadError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Upload Error</h3>
                  <p className="mt-1 text-sm text-red-700">{uploadError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Display */}
          {uploadSuccess && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Upload Successful! ✅</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p><strong>Institution:</strong> {uploadSuccess.institution}</p>
                    <p><strong>File:</strong> {uploadSuccess.fileName || uploadSuccess.file_name}</p>
                    <p><strong>IPFS CID:</strong> {uploadSuccess.cid}</p>
                    <p><strong>File Hash:</strong> {uploadSuccess.fileHash || uploadSuccess.file_hash}</p>
                    
                    {/* Display OCR data if available */}
                    {(uploadSuccess.ocrData || uploadSuccess.ocr_data) && (
                      <div className="mt-3 p-3 bg-green-100 rounded border">
                        <h4 className="font-semibold mb-2">📄 Extracted Data (Stored as Metadata):</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {(uploadSuccess.studentName || uploadSuccess.student_name) && <p><strong>Student:</strong> {uploadSuccess.studentName || uploadSuccess.student_name}</p>}
                          {(uploadSuccess.rollNumber || uploadSuccess.roll_number) && <p><strong>Roll No:</strong> {uploadSuccess.rollNumber || uploadSuccess.roll_number}</p>}
                          {(uploadSuccess.certificateType || uploadSuccess.certificate_type) && <p><strong>Type:</strong> {uploadSuccess.certificateType || uploadSuccess.certificate_type}</p>}
                          {uploadSuccess.marks && <p><strong>Marks:</strong> {uploadSuccess.marks}</p>}
                        </div>
                      </div>
                    )}
                    
                    <a
                      href={uploadSuccess.ipfsUrl || uploadSuccess.ipfs_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      View on IPFS
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
